/**
 * Returns true if `value` is a valid in-progress decimal entry with at most
 * `maxDecimals` digits after the decimal point. Permits empty string and
 * partial entries like "1." so the user can type naturally.
 *
 * Use in controlled input onChange handlers to reject keystrokes that would
 * push precision beyond the cap, e.g.
 *   onChange={(e) => {
 *     if (isValidDecimalInput(e.target.value)) setValue(e.target.value);
 *   }}
 */
export function isValidDecimalInput(value: string, maxDecimals = 2): boolean {
  if (value === '') return true;
  const pattern = new RegExp(`^-?\\d*(?:\\.\\d{0,${maxDecimals}})?$`);
  return pattern.test(value);
}

/**
 * Returns `value` rounded to at most `maxDecimals` digits after the decimal
 * point, formatted as a string. Useful for read-side display when stored
 * values may have legacy higher precision. Returns the original input if it
 * isn't a finite number.
 */
export function roundDecimalForDisplay(
  value: string | number | null | undefined,
  maxDecimals = 2,
): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  const factor = 10 ** maxDecimals;
  return String(Math.round(n * factor) / factor);
}
